// src/components/parent/ParentDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaSearch, FaUserFriends, FaChild, FaUserEdit,
  FaBuilding, FaTimes, FaMapMarkerAlt, FaPhone, FaEnvelope,
  FaRegBell, FaHeart, FaBriefcase, FaTrash
} from 'react-icons/fa';
import { collection, query, where, orderBy, limit, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getParentWaitingList, getChildrenByParent, getParentApplications, getParentJobs, getJobApplications } from '../../services/firestore';

const ParentDashboard = () => {
  const { userProfile, currentUser } = useAuth();
  const [recentProviders, setRecentProviders] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [errorActivities, setErrorActivities] = useState(null);
  const [waitlists, setWaitlists] = useState([]);
  const [loadingWaitlists, setLoadingWaitlists] = useState(true);
  const [errorWaitlists, setErrorWaitlists] = useState(null);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [children, setChildren] = useState([]);
  const [jobListings, setJobListings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentApplications, setRecentApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(true);
  const [parentJobs, setParentJobs] = useState([]);
  const [deletingApplication, setDeletingApplication] = useState(null);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const navigate = useNavigate();
  
  // Fetch all necessary data for dashboard
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch children
        const childrenData = await getChildrenByParent(currentUser.uid);
        setChildren(childrenData || []);

        // Fetch waitlists
        const waitlistData = await getParentWaitingList(currentUser.uid);
        setWaitlists(waitlistData || []);
        setLoadingWaitlists(false);

        // Fetch job listings
        const jobsQuery = query(
          collection(db, 'jobs'),
          where('parentUid', '==', currentUser.uid),
          where('status', '!=', 'deleted')
        );
        const jobsSnapshot = await getDocs(jobsQuery);
        const activeJobs = jobsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setJobListings(activeJobs);

        // Fetch notifications
        const notificationsQuery = query(
          collection(db, 'notifications'),
          where('userId', '==', currentUser.uid)
        );
        const notificationsSnapshot = await getDocs(notificationsQuery);
        setNotifications(notificationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setErrorActivities(error.message);
      } finally {
        setLoading(false);
        setLoadingActivities(false);
      }
    };

    if (currentUser?.uid) {
      fetchDashboardData();
    }
  }, [currentUser]);

  // Fetch parent's waitlists
  useEffect(() => {
    if (!currentUser) return;
    
    const fetchWaitlists = async () => {
      try {
        setLoadingWaitlists(true);
        const waitlistData = await getParentWaitingList(currentUser.uid);
        setWaitlists(waitlistData);
      } catch (error) {
        console.error('Error fetching waitlists:', error);
        setErrorWaitlists(error);
      } finally {
        setLoadingWaitlists(false);
      }
    };
    
    fetchWaitlists();
  }, [currentUser]);
  
  useEffect(() => {
    if (!currentUser) return;
    const fetchActivities = async () => {
      try {
        setLoadingActivities(true);
        const q = query(
          collection(db, "messages"),
          where("participants", "array-contains", currentUser.uid),
          orderBy("createdAt", "desc"),
          limit(20)
        );
        const querySnapshot = await getDocs(q);
        const activities = [];
        const userIds = new Set();
        querySnapshot.forEach((doc) => {
          const message = doc.data();
          // Add both sender and receiver IDs to the set
          userIds.add(message.senderId);
          message.participants.forEach(id => userIds.add(id));
          activities.push({
            id: doc.id,
            type: 'message',
            senderId: message.senderId,
            receiverId: message.participants.find(id => id !== message.senderId),
            content: message.text,
            timestamp: message.createdAt,
            icon: '📬'
          });
        });
        // Fetch user names in batch
        const userIdArr = Array.from(userIds);
        const userNames = {};
        if (userIdArr.length > 0) {
          const usersRef = collection(db, "users");
          const usersQuery = query(usersRef, where("uid", "in", userIdArr));
          const usersSnapshot = await getDocs(usersQuery);
          usersSnapshot.forEach((doc) => {
            const user = doc.data();
            userNames[user.uid] = user.displayName || user.name || user.email || user.uid;
          });
        }
        // Attach user names to activities
        const activitiesWithNames = activities.map(activity => ({
          ...activity,
          senderName: userNames[activity.senderId] || activity.senderId,
          receiverName: userNames[activity.receiverId] || activity.receiverId
        }));
        // Group by senderId and receiverId and keep only the most recent message per pair
        const uniqueActivities = [];
        const seenPairs = new Set();
        for (const activity of activitiesWithNames) {
          // Create a consistent pair key regardless of who sent the message
          const pairKey = [activity.senderId, activity.receiverId].sort().join('-');
          if (!seenPairs.has(pairKey)) {
            uniqueActivities.push(activity);
            seenPairs.add(pairKey);
          }
          if (uniqueActivities.length >= 5) break;
        }
        setRecentActivities(uniqueActivities);
      } catch (error) {
        setErrorActivities(error);
      } finally {
        setLoadingActivities(false);
      }
    };
    fetchActivities();
  }, [currentUser]);
  
  useEffect(() => {
    if (!currentUser) return;
    const fetchRecentApplications = async () => {
      setLoadingApplications(true);
      try {
        // 1. Get all jobs posted by this parent
        const parentJobs = await getParentJobs(currentUser.uid);
        const jobIds = parentJobs.map(job => job.id);
        if (jobIds.length === 0) {
          setFilteredApplications([]);
          setLoadingApplications(false);
          return;
        }
        // 2. Get all applications for these jobs
        let allApplications = [];
        for (const jobId of jobIds) {
          const jobApplications = await getJobApplications(jobId);
          allApplications.push(...jobApplications);
        }
        // 3. Sort and take the most recent 3
        allApplications.sort((a, b) => {
          const dateA = a.createdAt?.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(a.createdAt);
          const dateB = b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(b.createdAt);
          return dateB - dateA;
        });
        setFilteredApplications(allApplications.slice(0, 3));
      } catch (err) {
        setFilteredApplications([]);
      } finally {
        setLoadingApplications(false);
      }
    };
    fetchRecentApplications();
  }, [currentUser]);
  
  useEffect(() => {
    if (!currentUser) return;
    // Fetch jobs owned by the parent
    getParentJobs(currentUser.uid).then(setParentJobs);
  }, [currentUser]);
  
  const handleActivityClick = (activity) => {
    // Navigate to messages page with the conversation ID
    navigate('/messages', { 
      state: { 
        conversationId: activity.id,
        participants: [activity.senderId, activity.receiverId]
      }
    });
  };

  const toggleWaitlistModal = () => {
    setShowWaitlistModal(!showWaitlistModal);
  };
  
  // Reusable components
  const StatCard = ({ icon: Icon, number, label, className, onClick }) => (
    <div 
      className={`stat-card ${className} ${onClick ? 'clickable' : ''}`}
      onClick={() => { if (onClick) { console.log('StatCard clicked:', label); onClick(); } }}
    >
      <div className="stat-icon"><Icon /></div>
      <div className="stat-number">{number}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
  
  const ProviderCard = ({ provider }) => (
    <div className="provider-card" key={provider.id}>
      <div className="provider-header">
        <h3>{provider.name}</h3>
        <div className="provider-tags">
          {provider.type === 'center' && <span className="tag center-tag">Daycare Center</span>}
          {provider.type === 'home' && <span className="tag home-tag">Daycare Home</span>}
          {provider.type === 'nanny' && <span className="tag home-tag">Nanny</span>}
          {provider.licensed && <span className="tag licensed-tag">Licensed</span>}
        </div>
      </div>
      
      <div className="provider-details">
        <div className="provider-info">
          <div className="provider-rating">
            <span className="stars">{'★'.repeat(Math.floor(provider.rating))}{provider.rating % 1 >= 0.5 ? '½' : ''}</span>
            <span className="reviews-count">({provider.reviewCount} reviews)</span>
          </div>
        </div>
        
        <div className="provider-actions">
          <Link to={`/parent/search/providers/${provider.id}`} className="btn btn-outline btn-sm">View Profile</Link>
          <button className="btn btn-primary btn-sm">Contact</button>
        </div>
      </div>
    </div>
  );
  
  const ChildCard = ({ child, index }) => (
    <div className="child-card" key={index}>
      <div className="child-info">
        <h3>{child.name}</h3>
        <p><strong>Age:</strong> {calculateAge(child.dateOfBirth)}</p>
        {child.gender && <p><strong>Gender:</strong> {child.gender}</p>}
      </div>
      <div className="child-actions">
        <Link to={`/parent/children/edit/${index}`} className="btn btn-outline btn-sm">Edit</Link>
      </div>
    </div>
  );
  
  const FeedItem = ({ icon, title, time, link, action }) => (
    <div className="feed-item">
      <div className="feed-icon">{icon}</div>
      <div className="feed-content">
        <div className="feed-title">{title}</div>
        <div className="feed-time">{time}</div>
        <div className="feed-actions">
          <Link to={link}>{action}</Link>
        </div>
      </div>
    </div>
  );
  
  const QuickActionCard = ({ to, icon: Icon, title, description }) => {
    const handleClick = (e) => {
      e.preventDefault();
      navigate(to);
    };
    
    return (
      <div 
        className="quick-action-card no-wiggle" 
        onClick={handleClick}
        role="button"
        tabIndex={0}
      >
        <div className="quick-action-icon">
          <Icon />
        </div>
        <div className="quick-action-content">
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
      </div>
    );
  };

  // Quick actions configuration
  const quickActions = [
    {
      to: '/parent/search/providers', 
      icon: FaSearch,
      title: 'Find Providers', 
      desc: 'Search for childcare centers and dayhomes'
    },
    {
      to: '/parent/search/jobseekers', 
      icon: FaUserFriends,
      title: 'Find Caregivers', 
      desc: 'Search for nannies and babysitters'
    },
    {
      to: '/parent/children', 
      icon: FaChild,
      title: 'My Children', 
      desc: 'Manage your children\'s profiles'
    },
    {
      to: '/parent/jobs', 
      icon: FaBriefcase,
      title: 'Post Jobs', 
      desc: 'Create and manage job listings'
    }
  ];

  // Waitlist modal component
  const WaitlistModal = ({ waitlists, onClose }) => (
    <div className="modal-overlay">
      <div className="waitlist-modal">
        <div className="modal-header">
          <h2>Your Waitlists</h2>
          <button className="modal-close-button" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="modal-body">
          {waitlists.length > 0 ? (
            <div className="waitlist-providers-list">
              {waitlists.map((waitlist) => (
                <div className="waitlist-provider-card" key={waitlist.id}>
                  <div className="waitlist-provider-header">
                    <h3>{waitlist.provider?.name || 'Unknown Provider'}</h3>
                    <div className="waitlist-status">
                      <span className={`status-badge status-${waitlist.status || 'pending'}`}>
                        {waitlist.status || 'Pending'}
                      </span>
                    </div>
                  </div>
                  <div className="waitlist-provider-body">
                    {waitlist.provider && (
                      <>
                        <p className="waitlist-provider-address">
                          <FaMapMarkerAlt className="icon" />
                          {waitlist.provider.address || 'Address not provided'}
                        </p>
                        <div className="waitlist-provider-child">
                          <p><strong>Child:</strong> {waitlist.childData?.name || 'Not specified'}</p>
                          {waitlist.position && (
                            <p><strong>Position:</strong> {waitlist.position}</p>
                          )}
                          <p><strong>Requested:</strong> {waitlist.createdAt ? new Date(waitlist.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown date'}</p>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="waitlist-provider-actions">
                    <Link 
                      to={`/provider/${waitlist.provider?.id || waitlist.providerUid}`} 
                      className="btn btn-outline btn-sm"
                    >
                      View Provider
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>You haven't joined any waitlists yet.</p>
              <Link to="/parent/search/providers" className="btn btn-primary">
                Find Providers
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
  
  // Activity feed data - This would also come from the API in a real application
  const activityFeed = [];
  
  // Helper to calculate age from dateOfBirth
  const calculateAge = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      if (date.seconds) {
        return new Date(date.seconds * 1000).toLocaleDateString();
      }
      return new Date(date).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };
  
  // Stats data - showing children and waitlists
  const stats = [
    { 
      icon: FaChild, 
      number: loading ? '...' : children.length, 
      label: 'Children', 
      className: 'primary',
      onClick: children.length > 0 ? () => navigate('/parent/children') : null
    },
    { 
      icon: FaBuilding, 
      number: loading ? '...' : waitlists.length, 
      label: 'Waitlists', 
      className: 'info',
      onClick: waitlists.length > 0 ? toggleWaitlistModal : null
    },
    { 
      icon: FaBriefcase, 
      number: loading ? '...' : jobListings.length, 
      label: 'Job Listings', 
      className: 'secondary',
      onClick: () => navigate('/parent/jobs')
    }
  ];
  
  // Add delete application handler
  const handleDeleteApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    
    try {
      setDeletingApplication(applicationId);
      await deleteDoc(doc(db, 'applications', applicationId));
      // Update local state
      setRecentApplications(prev => prev.filter(app => app.id !== applicationId));
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('Failed to delete application. Please try again.');
    } finally {
      setDeletingApplication(null);
    }
  };
  
  return (
    <div className="dashboard-container">
      <div className="dashboard-welcome-section">
        <div className="welcome-text">
          <h1>Dashboard</h1>
          <p className="dashboard-welcome">Welcome back, {userProfile?.displayName || 'parent'}!</p>
          <p className="dashboard-description">Find childcare providers and manage your childcare needs</p>
        </div>
        <div className="profile-edit-section">
          <Link to={`/parent/profile/${currentUser?.uid}`} className="edit-profile-button">
            <FaUserEdit size={16} className="icon-margin-right" />
            Edit Profile
          </Link>
        </div>
      </div>
      
      {/* Stats Overview */}
      <div className="dashboard-stats-section">
        <div className="stats-grid">
          {stats.map((stat, i) => {
            const { onClick, ...rest } = stat;
            return <StatCard key={i} {...rest} />;
          })}
        </div>
      </div>
      
      {/* Quick Actions Section */}
      <div className="dashboard-quick-actions">
        <h2 className="section-title">Quick Actions</h2>
        <div className="quick-actions-grid">
          {quickActions.map((action, index) => (
            <QuickActionCard 
              key={index}
              to={action.to}
              icon={action.icon}
              title={action.title}
              description={action.desc}
            />
          ))}
        </div>
      </div>

      <div className="dashboard-layout">
        <div className="dashboard-main">
          {/* My Children */}
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">My Children</h2>
              <div className="section-actions">
                <Link to="/parent/children" className="btn btn-outline btn-sm">Manage</Link>
              </div>
            </div>
            
            <div className="children-list">
              {children.length > 0 ? (
                children.map((child, index) => <ChildCard key={index} child={child} index={index} />)
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">👶</div>
                  <p className="empty-state-text">No children added yet.</p>
                  <Link to="/parent/children" className="btn btn-primary">Add Child</Link>
                </div>
              )}
            </div>
          </section>
          
          {/* Recent Job Applications */}
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">Recent Job Applications</h2>
              <div className="section-actions">
                <Link to="/parent/applications" className="btn btn-outline btn-sm">View All</Link>
              </div>
            </div>
            <div className="card">
              <div className="card-body p-0 scrollable-table-container">
                {loadingApplications ? (
                  <div className="loading-container">
                    <p>Loading applications...</p>
                  </div>
                ) : filteredApplications.length > 0 ? (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Applicant</th><th>Job Title</th><th>Applied</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApplications.map(application => (
                        <tr key={application.id} className="application-row">
                          <td>{application.jobSeekerName || 'N/A'}</td>
                          <td>{application.jobTitle || 'N/A'}</td>
                          <td>{formatDate(application.createdAt)}</td>
                          <td>
                            <div className="table-actions">
                              <button 
                                onClick={() => navigate(`/jobseeker/${application.jobSeekerUid}`)}
                                className="btn btn-outline btn-sm"
                              >
                                View Profile
                              </button>
                              <button
                                onClick={() => handleDeleteApplication(application.id)}
                                className="btn btn-danger btn-sm"
                                disabled={deletingApplication === application.id}
                              >
                                <FaTrash /> {deletingApplication === application.id ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              ) : (
                <div className="empty-state">
                    <p>No applications received yet.</p>
                </div>
              )}
              </div>
            </div>
          </section>
        </div>
        
        <div className="dashboard-sidebar">
          {/* Recent Activity */}
          <div className="card activity-card">
            <div className="card-header">
              <h2 className="card-title">Recent Activities</h2>
            </div>
            <div className="card-body">
              <div className="activity-feed">
                {loadingActivities ? (
                  <div className="loading-container">
                    <p>Loading activities...</p>
                  </div>
                ) : recentActivities.length > 0 ? (
                  recentActivities.map(activity => (
                    <div 
                      className="feed-item" 
                      key={activity.id}
                      onClick={() => handleActivityClick(activity)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="feed-icon">{activity.icon}</div>
                      <div className="feed-content">
                        <div className="feed-title">
                          {activity.senderId === currentUser.uid 
                            ? `You sent a message to ${activity.receiverName}`
                            : `${activity.senderName} sent you a message`}
                        </div>
                        <div className="feed-time">{activity.timestamp && (new Date(activity.timestamp.seconds ? activity.timestamp.seconds * 1000 : activity.timestamp)).toLocaleDateString()}</div>
                        {activity.content && (
                          <div className="feed-message">{activity.content}</div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="feed-item">
                    <div className="feed-icon">🎉</div>
                    <div className="feed-content">
                      <div className="feed-title">Welcome to your dashboard!</div>
                      <div className="feed-time">Just now</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Waitlist Modal */}
      {showWaitlistModal && (
        <WaitlistModal 
          waitlists={waitlists}
          onClose={toggleWaitlistModal}
        />
      )}
    </div>
  );
};

export default ParentDashboard;